// src/components/home/QuickTiles.js
// 4 bannieres cliquables ciblees (Massage, Coiffure, Animalerie, Restaurants...)
// Remplace l'ancien bloc "Comment ca marche" (3 pictogrammes Trouvez / Achetez / Profitez)
// qui prenait beaucoup de place pour un message generique sans lien cliquable.
//
// Le bloc affiche 4 tuiles a la fois et FAIT DEFILER toutes les categories par
// pages de 4, une page toutes les 40 secondes. Chaque categorie a donc sa
// chance d'etre vue, sans allonger la page.
//
// Source des tuiles, par ordre de priorite :
//   1) Admin > Publicites, emplacement "home_tiles" (image, titre, sous-titre, lien, dates,
//      comptage des clics) -> AUCUN changement backend, on reutilise le champ `position` des ads.
//      Les publicites occupent TOUJOURS la premiere page : ce sont les emplacements payes,
//      ils ne doivent pas disparaitre dans la rotation.
//   2) Les categories, triees par dealsCount DECROISSANT (jamais par ordre alphabetique :
//      on montre d'abord celles qui ont le plus a offrir). Le bloc n'est donc jamais vide.
//
// FR / AR (RTL, police Cairo geree globalement par index.css).
// Maxula — AbodyStudio Limited

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiArrowLeft } from 'react-icons/fi';

import { adAPI, categoryAPI } from '../../services/api';
import { normalizeImagePath } from '../../utils/imageUtils';
import { renderCategoryIcon } from '../../utils/categoryIcons';
import { localizedName } from '../../utils/localizedName';
import { useLanguage } from '../../i18n/LanguageContext';

const PLACEMENT = 'home_tiles';
const MAX_TILES = 4;          // tuiles affichees simultanement
const ROTATE_MS = 40000;      // 40 s par page
const FADE_MS = 400;          // duree du fondu entre deux pages

// Palette de la maquette, dans l'ordre : violet, rose, turquoise, orange.
// Les couleurs sont appliquees en STYLE INLINE et non par des classes Tailwind
// construites dynamiquement : une classe du type `from-${color}` serait supprimee
// par la purge Tailwind en production (le fond deviendrait transparent).
//
// `rgb` est la couleur brute, les opacites sont calculees plus bas. Le voile
// precedent montait a 0.62 des le haut de la carte et noyait completement la
// photo — la teinte etait si dense qu'une carte avec image et une carte sans
// se ressemblaient. Le voile est desormais quasi transparent en haut (0.10) et
// ne se densifie qu'en bas, la ou le texte se pose.
const THEMES = [
  { rgb: '124, 58, 237', solid: '#6d28d9' },
  { rgb: '225, 29, 123', solid: '#e11d7b' },
  { rgb: '13, 148, 136', solid: '#0d9488' },
  { rgb: '234, 88, 12', solid: '#ea580c' },
];

// Deux couches distinctes, et la distinction compte :
//   - TEINTE : donne la couleur de la categorie, tres legere en haut pour ne pas
//     effacer la photo.
//   - LISIBILITE : un noir neutre concentre sur le bas. C'est lui qui garantit le
//     contraste du texte ; le faire porter par la couleur obligerait a remonter
//     l'opacite de la teinte et on reviendrait a l'aplat.
const tintLayer = (rgb) =>
  `linear-gradient(180deg, rgba(${rgb}, 0.10) 0%, rgba(${rgb}, 0.22) 40%, rgba(${rgb}, 0.72) 78%, rgba(${rgb}, 0.90) 100%)`;
const readabilityLayer =
  'linear-gradient(180deg, rgba(0,0,0,0.28) 0%, rgba(0,0,0,0) 32%, rgba(0,0,0,0.30) 82%, rgba(0,0,0,0.45) 100%)';

// Animations : injectees en <style> plutot qu'en classes Tailwind. Tailwind n'a
// pas ces keyframes en base et il n'y a pas de compilateur ici — une classe
// personnalisee serait simplement ignoree.
const ANIMATION_CSS = `
@keyframes qtRise {
  from { opacity: 0; transform: translateY(18px) scale(0.985); }
  to   { opacity: 1; transform: none; }
}
@keyframes qtZoom {
  from { transform: scale(1); }
  to   { transform: scale(1.12); }
}
@keyframes qtPop {
  0%   { opacity: 0; transform: scale(0.4) rotate(-25deg); }
  60%  { transform: scale(1.12) rotate(4deg); }
  100% { opacity: 1; transform: none; }
}
/* fill-mode "backwards" et NON "both" : une animation qui remplit vers l'avant
   garde la main sur transform apres la fin et neutraliserait purement et
   simplement le translateY du survol. Avec "backwards", l'etat de depart est
   applique avant le demarrage (pas de flash) et la propriete redevient normale
   une fois l'animation finie. */
.qt-card { animation: qtRise 620ms cubic-bezier(0.22, 1, 0.36, 1) backwards; }
.qt-card:hover { transform: translateY(-8px); }
/* Ken Burns lent pendant les 40 s d'affichage : la photo respire au lieu d'etre
   figee, sans jamais bouger assez vite pour distraire de la lecture. Ici on
   garde "both" : le zoom doit rester acquis a la fin. */
.qt-photo { animation: qtZoom 40s ease-out both; }
.qt-card:hover .qt-photo { animation-play-state: paused; }
.qt-badge { animation: qtPop 620ms cubic-bezier(0.22, 1, 0.36, 1) backwards; }
.qt-card:hover .qt-badge { transform: scale(1.08); }
.qt-arrow { transition: transform 300ms ease-out; }
.qt-card:hover .qt-arrow { transform: translateX(4px); }
[dir="rtl"] .qt-card:hover .qt-arrow { transform: translateX(-4px); }
/* Reflet diagonal au survol : la carte est cliquable, autant que ca se voie. */
.qt-sheen {
  position: absolute; inset: 0; pointer-events: none;
  background: linear-gradient(115deg, transparent 38%, rgba(255,255,255,0.30) 50%, transparent 62%);
  transform: translateX(-120%);
  transition: transform 900ms ease-out;
}
.qt-card:hover .qt-sheen { transform: translateX(120%); }
/* Description masquee sur mobile : elle ajoute deux lignes par carte, soit
   presque 100 px de defilement pour quatre tuiles, alors que le titre suffit a
   identifier l'univers. Le clamp doit passer par une classe et non un style
   inline, sinon le display:-webkit-box ecraserait le masquage. */
.qt-desc { display: none; }
@media (min-width: 640px) {
  .qt-desc {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
}
@media (prefers-reduced-motion: reduce) {
  .qt-card, .qt-photo, .qt-badge { animation: none !important; }
  .qt-card, .qt-arrow, .qt-sheen, .qt-photo { transition: none !important; transform: none !important; }
}
`;

// Neutralise les fausses valeurs stockees en base ("null", "NULL", "undefined", "").
const clean = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  if (!s) return null;
  const low = s.toLowerCase();
  if (low === 'null' || low === 'undefined' || low === 'nan') return null;
  return s;
};

const isInternal = (url) =>
  typeof url === 'string' && url.startsWith('/') && !url.startsWith('//');

// Meme logique que AdBanner : un lien absolu enregistre en local
// ("http://localhost:3000/deals/x") doit redevenir un chemin interne en production,
// sinon le visiteur est envoye sur localhost.
const toRoute = (raw, fallback = '/deals') => {
  const s = clean(raw);
  if (!s) return fallback;
  if (isInternal(s)) return s;
  try {
    const origin =
      typeof window !== 'undefined' && window.location ? window.location.origin : 'http://localhost';
    const u = new URL(s, origin);
    const host = (u.hostname || '').toLowerCase();
    const isLocal =
      host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0' || host.endsWith('.local');
    const isSameOrigin =
      typeof window !== 'undefined' &&
      window.location &&
      host === String(window.location.hostname || '').toLowerCase();
    if (isLocal || isSameOrigin) return u.pathname + u.search + u.hash || fallback;
    return s;
  } catch (e) {
    return s;
  }
};

const localizedDescription = (cat, lang) => {
  if (!cat) return '';
  if (lang === 'ar' && clean(cat.descriptionAr)) return String(cat.descriptionAr).trim();
  return clean(cat.description) || '';
};

const QuickTiles = ({ className = '' }) => {
  const { t, lang, isRTL } = useLanguage();
  const [ads, setAds] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      // Les deux appels sont independants : si l'un echoue, l'autre alimente
      // quand meme le bloc (Promise.allSettled et non Promise.all).
      const [adRes, catRes] = await Promise.allSettled([
        adAPI.getActive(PLACEMENT),
        categoryAPI.getAll(),
      ]);
      if (!mounted) return;

      if (adRes.status === 'fulfilled') {
        const list = adRes.value?.data?.data;
        setAds(Array.isArray(list) ? list : []);
      }
      if (catRes.status === 'fulfilled') {
        const payload = catRes.value?.data;
        const list = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
        setCategories(list);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // ---------------------------------------------------------------
  // POOL COMPLET : toutes les tuiles candidates, pas seulement 4.
  // ---------------------------------------------------------------
  const allTiles = useMemo(() => {
    // 1) Publicites home_tiles — elles ouvrent toujours la rotation.
    const fromAds = ads
      .filter((a) => clean(a?.title) || clean(a?.image))
      .map((a) => ({
        key: `ad-${a._id ?? a.id}`,
        adId: a._id ?? a.id,
        title: clean(a.title) || '',
        // Sous-titre : champ "annonceur" en priorite, sinon le badge.
        subtitle: clean(a.advertiser) || clean(a.badge) || '',
        image: clean(a.image) ? normalizeImagePath(a.image) : null,
        icon: null,
        to: toRoute(a.link),
        cta: clean(a.ctaLabel) || t('home.tilesCta'),
      }));

    // 2) TOUTES les categories, et non plus les 4 premieres.
    // Les categories a 0 annonce sont ecartees : tant qu'on n'affichait que le
    // haut du classement elles ne sortaient jamais, mais avec la rotation elles
    // finiraient par passer a l'ecran et enverraient le visiteur sur une liste
    // vide — le pire resultat possible pour un raccourci.
    const used = new Set(fromAds.map((x) => String(x.title).toLowerCase()));
    const fromCats = [...categories]
      .filter((c) => c && (c.isActive === undefined || c.isActive))
      .filter((c) => Number(c?.dealsCount || 0) > 0)
      .filter((c) => !used.has(String(localizedName(c, lang)).toLowerCase()))
      .sort((a, b) => Number(b?.dealsCount || 0) - Number(a?.dealsCount || 0))
      .map((c) => ({
        key: `cat-${c._id ?? c.id}`,
        adId: null,
        title: localizedName(c, lang),
        subtitle: localizedDescription(c, lang),
        image: clean(c.image) ? normalizeImagePath(c.image) : null,
        icon: c.icon || null,
        to: `/deals?category=${c.slug ?? c._id ?? c.id}`,
        cta: t('home.tilesCta'),
      }));

    return [...fromAds, ...fromCats];
  }, [ads, categories, lang, t]);

  // ---------------------------------------------------------------
  // ROTATION par pages de 4
  // ---------------------------------------------------------------
  const pageCount = Math.max(1, Math.ceil(allTiles.length / MAX_TILES));
  const [page, setPage] = useState(0);
  const [fading, setFading] = useState(false);
  const fadeTimer = useRef(null);
  const tickTimer = useRef(null);
  // La page courante est aussi tenue dans une ref : le minuteur en a besoin, et
  // la lire via un updater setPage(p => ...) declencherait l'effet de bord deux
  // fois en StrictMode (React rejoue les updaters), donc deux fondus empiles.
  const pageRef = useRef(0);
  useEffect(() => { pageRef.current = page; }, [page]);

  // Le pool peut retrecir (changement de langue, categorie desactivee) : sans ce
  // garde-fou la page courante pointerait dans le vide et le bloc s'afficherait
  // vide au lieu de revenir au debut.
  useEffect(() => {
    setPage((p) => (p >= pageCount ? 0 : p));
  }, [pageCount]);

  const goTo = useCallback((next) => {
    if (fadeTimer.current) clearTimeout(fadeTimer.current);
    // Respecte le reglage systeme "reduire les animations" : on bascule sec.
    const reduce =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setPage(next);
      return;
    }
    setFading(true);
    fadeTimer.current = setTimeout(() => {
      setPage(next);
      setFading(false);
    }, FADE_MS);
  }, []);

  useEffect(() => {
    if (pageCount <= 1) return undefined;

    const start = () => {
      if (tickTimer.current) clearInterval(tickTimer.current);
      tickTimer.current = setInterval(() => {
        goTo((pageRef.current + 1) % pageCount);
      }, ROTATE_MS);
    };

    // Onglet en arriere-plan : on arrete le minuteur. Sinon il continue de
    // tourner et le visiteur qui revient tombe sur une page au hasard, parfois
    // en plein fondu.
    const onVisibility = () => {
      if (typeof document === 'undefined') return;
      if (document.hidden) {
        if (tickTimer.current) clearInterval(tickTimer.current);
        tickTimer.current = null;
      } else {
        start();
      }
    };

    start();
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility);
    }
    return () => {
      if (tickTimer.current) clearInterval(tickTimer.current);
      if (fadeTimer.current) clearTimeout(fadeTimer.current);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility);
      }
    };
  }, [pageCount, goTo]);

  // Clic sur une pastille : on va a la page demandee ET on relance le compteur
  // a zero, sinon le basculement automatique peut arriver une seconde apres.
  const jumpTo = (target) => {
    if (target === page) return;
    goTo(target);
    if (tickTimer.current) {
      clearInterval(tickTimer.current);
      tickTimer.current = setInterval(() => {
        goTo((pageRef.current + 1) % pageCount);
      }, ROTATE_MS);
    }
  };

  // Derniere page incomplete (9 categories = 4 + 4 + 1) : on la complete en
  // reprenant le debut du pool, sinon la grille finit sur une seule carte
  // orpheline a gauche et trois trous. On ne complete que si le pool depasse
  // 4 elements, sinon on dupliquerait une tuile deja affichee.
  const tiles = (() => {
    const start = page * MAX_TILES;
    const slice = allTiles.slice(start, start + MAX_TILES);
    if (slice.length === MAX_TILES || allTiles.length <= MAX_TILES) return slice;
    const filler = allTiles.slice(0, MAX_TILES - slice.length);
    return [...slice, ...filler];
  })();

  if (!allTiles.length) return null;

  const Arrow = isRTL ? FiArrowLeft : FiArrowRight;

  return (
    <section className={`py-6 sm:py-10 bg-[#fff3f8] ${className}`}>
      <style>{ANIMATION_CSS}</style>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-xl md:text-2xl font-bold text-center text-gray-900">
          {t('home.howItWorks')}
        </h2>
        <p className="text-center text-sm text-gray-500 mt-1">{t('home.tilesSub')}</p>

        <div
          className="mt-6 sm:mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 transition-opacity"
          style={{ opacity: fading ? 0 : 1, transitionDuration: `${FADE_MS}ms` }}
        >
          {tiles.map((tile, i) => {
            const theme = THEMES[i % THEMES.length];
            // La cle inclut la page : une tuile de remplissage peut apparaitre
            // deux fois dans la meme grille, deux cles identiques casseraient
            // le rendu React.
            const tileKey = `${tile.key}-p${page}-${i}`;
            const onClick = () => {
              if (!tile.adId) return;
              try {
                adAPI.trackClick(tile.adId);
              } catch (e) {}
            };

            // Decalage en cascade : les 4 cartes n'arrivent pas d'un bloc, elles
            // se posent l'une apres l'autre. C'est ce qui fait lire le bloc de
            // gauche a droite au lieu d'un simple clignotement.
            const delay = `${i * 90}ms`;

            const inner = (
              <>
                {/* Photo de fond */}
                {tile.image ? (
                  <img
                    src={tile.image}
                    alt=""
                    aria-hidden="true"
                    className="qt-photo absolute inset-0 h-full w-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  // Sans photo : degrade de la couleur du theme plutot qu'un
                  // aplat mort, pour que la carte reste presentable.
                  <div
                    className="absolute inset-0"
                    style={{
                      backgroundImage: `linear-gradient(150deg, rgba(${theme.rgb}, 1) 0%, rgba(${theme.rgb}, 0.72) 55%, rgba(0,0,0,0.35) 100%)`,
                    }}
                  />
                )}

                {/* Teinte de la categorie — legere en haut, la photo reste visible */}
                <div className="absolute inset-0" style={{ backgroundImage: tintLayer(theme.rgb) }} />
                {/* Contraste du texte, en noir neutre et concentre sur le bas */}
                <div className="absolute inset-0" style={{ backgroundImage: readabilityLayer }} />
                <span className="qt-sheen" aria-hidden="true" />

                <div className="relative z-10 flex h-full flex-col items-center justify-end px-3 pb-4 pt-5 text-center sm:px-5 sm:pb-6 sm:pt-8">
                  {/* Pastille d'icone blanche */}
                  <div
                    className="qt-badge mb-auto flex h-11 w-11 sm:h-14 sm:w-14 items-center justify-center rounded-full bg-white shadow-xl ring-4 ring-white/25 transition-transform duration-300"
                    style={{ animationDelay: `calc(${delay} + 140ms)` }}
                  >
                    <span style={{ color: theme.solid, lineHeight: 0 }}>
                      {renderCategoryIcon(tile.icon, 22, '')}
                    </span>
                  </div>

                  <h3
                    className="text-sm sm:text-xl font-extrabold leading-tight text-white"
                    style={{ textShadow: '0 2px 12px rgba(0,0,0,0.45)' }}
                  >
                    {tile.title}
                  </h3>

                  {/* Filet colore sous le titre : rappelle la couleur du theme
                      sans avoir a repeindre toute la carte. */}
                  <span className="mt-1.5 sm:mt-2 h-[3px] w-8 sm:w-10 rounded-full bg-white/70" aria-hidden="true" />

                  {tile.subtitle ? (
                    <p
                      className="qt-desc mt-2 text-sm text-white/95"
                      style={{ textShadow: '0 1px 8px rgba(0,0,0,0.45)' }}
                    >
                      {tile.subtitle}
                    </p>
                  ) : null}

                  <span
                    className="mt-3 sm:mt-4 inline-flex items-center gap-1.5 sm:gap-2 rounded-full bg-white px-3 py-1.5 text-[11px] sm:px-5 sm:py-2 sm:text-sm font-bold shadow-lg"
                    style={{ color: theme.solid }}
                  >
                    <span>{tile.cta}</span>
                    <Arrow size={13} className="qt-arrow" />
                  </span>
                </div>
              </>
            );

            const cardClasses =
              'qt-card group relative block h-[200px] sm:h-[300px] lg:h-[320px] overflow-hidden rounded-2xl ' +
              'shadow-lg ring-1 ring-black/5 transition-[transform,box-shadow] duration-300 hover:shadow-2xl';

            return isInternal(tile.to) ? (
              <Link
                key={tileKey}
                to={tile.to}
                onClick={onClick}
                className={cardClasses}
                title={tile.title}
                style={{ animationDelay: delay }}
              >
                {inner}
              </Link>
            ) : (
              <a
                key={tileKey}
                href={tile.to}
                target="_blank"
                rel="noreferrer"
                onClick={onClick}
                className={cardClasses}
                title={tile.title}
                style={{ animationDelay: delay }}
              >
                {inner}
              </a>
            );
          })}
        </div>

        {pageCount > 1 && (
          <div className="mt-6 flex justify-center gap-2">
            {Array.from({ length: pageCount }).map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => jumpTo(i)}
                aria-label={`${t('home.howItWorks')} ${i + 1} / ${pageCount}`}
                aria-current={i === page ? 'true' : undefined}
                className={`h-2 rounded-full transition-all ${
                  i === page ? 'w-6 bg-[#e11d7b]' : 'w-2 bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default QuickTiles;
